import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

interface Props {
  /** The session's rrweb events; the FullSnapshot is parsed into a tree. */
  events: unknown[];
}

interface DomNode {
  tag: string;
  attrs: string;
  text?: string;
  children: DomNode[];
}

/** rrweb serialized-node types. */
const NodeType = { Document: 0, DocumentType: 1, Element: 2, Text: 3, Comment: 5 };

interface SerializedNode {
  type: number;
  tagName?: string;
  attributes?: Record<string, unknown>;
  textContent?: string;
  childNodes?: SerializedNode[];
}

/** Converts an rrweb serialized snapshot node into a compact display tree. */
function fromSnapshot(node: SerializedNode): DomNode | null {
  if (node.type === NodeType.Element) {
    const attrs = node.attributes ?? {};
    const id = attrs.id ? `#${attrs.id}` : '';
    const cls =
      typeof attrs.class === 'string'
        ? '.' + attrs.class.trim().split(/\s+/).slice(0, 3).join('.')
        : '';
    const dom: DomNode = { tag: node.tagName ?? 'node', attrs: id + cls, children: [] };
    for (const child of node.childNodes ?? []) {
      const c = fromSnapshot(child);
      if (c) dom.children.push(c);
    }
    if (dom.children.length === 0) {
      const text = (node.childNodes ?? [])
        .filter((n) => n.type === NodeType.Text)
        .map((n) => n.textContent?.trim())
        .join(' ');
      if (text) dom.text = text.slice(0, 60);
    }
    return dom;
  }
  if (node.type === NodeType.Document) {
    const el = (node.childNodes ?? []).find((n) => n.type === NodeType.Element);
    return el ? fromSnapshot(el) : null;
  }
  return null;
}

function TreeNode({ node, level }: { node: DomNode; level: number }) {
  const [open, setOpen] = useState(level < 3);
  const hasChildren = node.children.length > 0;
  return (
    <Box sx={{ pl: level * 1.5, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
      <Box
        component="span"
        sx={{ cursor: hasChildren ? 'pointer' : 'default', userSelect: 'none' }}
        onClick={() => hasChildren && setOpen((o) => !o)}
      >
        <Box component="span" sx={{ color: '#6b7280', width: 12, display: 'inline-block' }}>
          {hasChildren ? (open ? '▾' : '▸') : ' '}
        </Box>
        <Box component="span" sx={{ color: '#2ec2b3' }}>
          {node.tag}
        </Box>
        <Box component="span" sx={{ color: '#22c55e' }}>
          {node.attrs}
        </Box>
        {node.text && (
          <Box component="span" sx={{ color: '#9ca3af' }}>
            {' '}
            "{node.text}"
          </Box>
        )}
      </Box>
      {open && node.children.map((c, i) => <TreeNode key={i} node={c} level={level + 1} />)}
    </Box>
  );
}

/**
 * DOM inspector for the replay. Parses the recorded rrweb FullSnapshot into a
 * collapsible element tree — the reconstructed page structure the replay is
 * built from — independent of the player's render state.
 */
export function DomInspector({ events }: Props) {
  const root = useMemo(() => {
    const snapshot = (events as Array<{ type: number; data?: { node?: SerializedNode } }>).find(
      (e) => e.type === 2 && e.data?.node,
    );
    return snapshot?.data?.node ? fromSnapshot(snapshot.data.node) : null;
  }, [events]);

  if (!root) {
    return (
      <Typography sx={{ p: 2 }} color="text.secondary">
        No DOM snapshot in this session.
      </Typography>
    );
  }
  return (
    <Box sx={{ p: 1, maxHeight: 520, overflow: 'auto' }}>
      <TreeNode node={root} level={0} />
    </Box>
  );
}
